import { useEffect, useRef, useState } from 'react';
import { AuthLayout, Banner, Button, Spinner, Stack, Surface, Text } from '../ui';
import { useNavigate } from '@tanstack/react-router';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

/**
 * "Sign in with Google" (OIDC) redirect landing (`/oauth/google/login-callback`,
 * issue #56). Unlike the mailbox-connect callback (OAuthCallbackPage), this runs
 * OUTSIDE the authed shell — the user is not logged in yet. Google redirects here
 * with `?code&state`; this page:
 *   1. reads `code` + `state` (or an `error` if the user declined);
 *   2. posts them to `/api/auth/google/callback`, which verifies the signed state,
 *      exchanges the code, domain-gates, find-or-creates the user, and returns a
 *      session — the ApiClient stores the token (localStorage `ehe.token`);
 *   3. refreshes the auth context (`me`) and routes into `/app`.
 *
 * On failure it shows the error with a link back to /login so the fallback path
 * (email/password) is always reachable.
 */
export function GoogleLoginCallbackPage() {
  const navigate = useNavigate();
  const { onAuthenticated } = useAuth();
  const ran = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const oauthError = params.get('error');

    if (oauthError) {
      setError(`Google sign-in was cancelled or denied (${oauthError}).`);
      return;
    }
    if (!code || !state) {
      setError('Google sign-in failed: missing authorization code.');
      return;
    }

    void api
      .completeGoogleLogin(code, state)
      .then(() => onAuthenticated())
      .then(() => navigate({ to: '/app/dashboard' }))
      .catch((err) =>
        setError(
          err instanceof ApiError ? `Google sign-in failed: ${err.message}` : 'Google sign-in failed.',
        ),
      );
  }, [navigate, onAuthenticated]);

  return (
    <AuthLayout maxWidth="28rem" minHeight="60vh">
      <Surface tone="default" bordered radius="lg" elevation="sm" padding="8">
        {error ? (
          <Stack gap="4">
            <Banner tone="danger" role="alert" data-testid="google-login-error">
              {error}
            </Banner>
            <Button variant="primary" onClick={() => void navigate({ to: '/login' })}>
              Back to sign in
            </Button>
          </Stack>
        ) : (
          <Stack gap="3" align="center">
            <Spinner size="lg" />
            <Text tone="secondary">Finishing Google sign-in…</Text>
          </Stack>
        )}
      </Surface>
    </AuthLayout>
  );
}
